// Graphics presets, auto-detection and dynamic resolution (RALLY_GOAL.md §11, §14).

export type PresetName = 'low' | 'medium' | 'high' | 'ultra';

export interface QualitySettings {
  preset: PresetName;
  /** Upper bound of the render scale (dynamic resolution only goes below it). */
  renderScale: number;
  dynamicResolution: boolean;
  /** Shadow cascades (0 = no shadows) and map size. */
  shadowCascades: number;
  shadowMapSize: number;
  shadowDistance: number;
  /** Tree/rock view distance multiplier. */
  vegetation: number;
  /** Grass radius around the camera (m), 0 = none. */
  grassRadius: number;
  ao: boolean;
  particles: number;
  /** Max device pixel ratio honoured. */
  maxPixelRatio: number;
  /** Camera motion blur while driving, depth of field on the showcase (Ultra, P1-4). */
  motionBlur: boolean;
  dof: boolean;
  /** Fog distance multiplier (longer view distance on Ultra). */
  fogScale: number;
}

export const PRESETS: Record<PresetName, QualitySettings> = {
  low: { preset: 'low', renderScale: 0.75, dynamicResolution: true, shadowCascades: 1, shadowMapSize: 1024, shadowDistance: 60, vegetation: 0.55, grassRadius: 0, ao: false, particles: 0.5, maxPixelRatio: 1, motionBlur: false, dof: false, fogScale: 1 },
  medium: { preset: 'medium', renderScale: 0.85, dynamicResolution: true, shadowCascades: 2, shadowMapSize: 1536, shadowDistance: 160, vegetation: 0.8, grassRadius: 30, ao: false, particles: 0.85, maxPixelRatio: 1.25, motionBlur: false, dof: false, fogScale: 1 },
  high: { preset: 'high', renderScale: 1, dynamicResolution: true, shadowCascades: 3, shadowMapSize: 2048, shadowDistance: 260, vegetation: 1, grassRadius: 46, ao: true, particles: 1, maxPixelRatio: 1.5, motionBlur: false, dof: false, fogScale: 1 },
  // Ultra (P1-4): longer view distance — trees out to ~1.4 km as baked billboard impostors and the
  // fog pushed back — plus camera motion blur while driving and depth of field on the showcase.
  ultra: { preset: 'ultra', renderScale: 1, dynamicResolution: true, shadowCascades: 3, shadowMapSize: 2048, shadowDistance: 320, vegetation: 1.3, grassRadius: 60, ao: true, particles: 1, maxPixelRatio: 2, motionBlur: true, dof: true, fogScale: 1.4 },
};

/**
 * Pick a starting preset from the GPU name and device: software renderers and phones → Low,
 * integrated GPUs and Apple M-series → Medium, recognised discrete GPUs → High. Dynamic resolution
 * then keeps the frame rate on target.
 */
export function detectPreset(gpuName: string, backend: string, touchOnly: boolean): PresetName {
  const g = gpuName.toLowerCase();
  if (/swiftshader|llvmpipe|software|basic render|microsoft basic/.test(g)) return 'low';
  if (touchOnly) return 'low';
  if (/rtx|gtx 1[6-9]|gtx 2|radeon rx|rx [5-9]\d{3}|arc a[57]|quadro|titan/.test(g)) return 'high';
  if (/apple m[2-9] (pro|max|ultra)/.test(g)) return 'high';
  void backend;
  return 'medium';
}

/**
 * Dynamic resolution: steer the scene render scale (0.5 … preset max) so the frame time stays under
 * the target. Uses GPU time when the backend reports it, otherwise the frame interval.
 */
export class DynamicResolution {
  // Initialised so V8 keeps these as unboxed doubles (see Drivetrain.engineOmega).
  scale = 1;
  max = 1;
  readonly min = 0.5;
  enabled = true;
  target = 1000 / 60;
  private readonly samples = new Float32Array(30);
  private readonly sorted = new Float32Array(30);
  private n = 0;
  private cooldown = 0;

  constructor(max: number) {
    this.max = max;
    this.scale = max;
  }

  /** Feed one frame's cost (ms); returns true when the scale changed. */
  sample(frameMs: number, dt: number): boolean {
    if (!this.enabled) {
      if (this.scale !== this.max) {
        this.scale = this.max;
        return true;
      }
      return false;
    }
    this.samples[this.n++ % this.samples.length] = frameMs;
    this.cooldown -= dt;
    if (this.n < this.samples.length || this.cooldown > 0 || this.n % 15 !== 0) return false;
    this.sorted.set(this.samples);
    this.sorted.sort();
    const p80 = this.sorted[Math.floor(this.sorted.length * 0.8)];
    let next = this.scale;
    if (p80 > this.target * 1.12) next = this.scale - 0.125;
    else if (p80 < this.target * 0.72) next = this.scale + 0.125;
    next = Math.max(this.min, Math.min(this.max, next));
    if (next !== this.scale) {
      this.scale = next;
      this.cooldown = 1.0;
      return true;
    }
    return false;
  }
}
